import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, X } from 'lucide-react';
import dayjs from 'dayjs';
import { Input } from "../../../components/DesignSystem/components/Input";
import { Button } from "../../../components/DesignSystem/components/Button";
import { Heading, Text } from "../../../components/DesignSystem/components/Typography";

const ITEM_HEIGHT = 56;

const YEARS = Array.from({ length: 80 }, (_, i) => (dayjs().year() - 19) - i);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];


export const CustomDatePicker = ({
    value,
    onChange,
    error,
    disabled,
    label,
    labelClassName,
    mode = "light",
    maxDate
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // If native date string is "YYYY-MM-DD"
    const parsedDate = value ? dayjs(value, "YYYY-MM-DD") : null;
    const [tempSelectedDate, setTempSelectedDate] = useState(value || null);
    
    // Evaluate robust fallback 19 years ago to prevent 0 year calculations
    const defaultYear = dayjs().subtract(19, 'year').year();

    const [previewYear, setPreviewYear] = useState(parsedDate ? parsedDate.year() : defaultYear);
    const [previewMonth, setPreviewMonth] = useState(parsedDate ? parsedDate.month() : 0);
    const [view, setView] = useState('years'); // 'years' | 'months'

    // Update temp Selected date cleanly upon opening the Picker overlay.
    useEffect(() => {
        if (showModal) {
            setTempSelectedDate(value || null);
            if (value) {
                const p = dayjs(value, "YYYY-MM-DD");
                setPreviewYear(p.year());
                setPreviewMonth(p.month());
            } else {
                setPreviewYear(defaultYear);
                setPreviewMonth(0);
            }
        }
    }, [showModal, value, defaultYear]);

    const currentYear = dayjs().year();
    const maxAllowedYear = currentYear - 19;
    
    // Create years from maxAllowedYear down to maxAllowedYear - 80
    // Using memoized versions or constants to prevent re-renders
    const years = YEARS;
    const months = MONTHS;
    const fullMonths = FULL_MONTHS;

    const rightScrollRef = useRef(null);
    const [listContainerHeight, setListContainerHeight] = useState(400);

    // Initial positioning and capturing height
    useEffect(() => {
        if (showModal && rightScrollRef.current) {
            const h = rightScrollRef.current.clientHeight;
            setListContainerHeight(h);

            const index = view === 'years' 
                ? years.indexOf(previewYear)
                : previewMonth;
            
            if (index !== -1) {
                const scrollTo = index * ITEM_HEIGHT;
                // use setTimeout to ensure render is flush before scrollTo
                setTimeout(() => {
                    if (rightScrollRef.current) rightScrollRef.current.scrollTop = scrollTo > 0 ? scrollTo : 0;
                }, 10);
            }
        }
    }, [showModal, view, previewYear, previewMonth]);

    const handleScroll = (e) => {
        if (!e.target) return;
        const scrollTop = e.target.scrollTop;
        
        let index = Math.round(scrollTop / ITEM_HEIGHT);
        
        if (view === 'years') {
            if (index < 0) index = 0;
            if (index >= years.length) index = years.length - 1;
            if (years[index] !== previewYear) {
                setPreviewYear(years[index]);
            }
        } else if (view === 'months') {
            if (index < 0) index = 0;
            if (index >= months.length) index = months.length - 1;
            if (index !== previewMonth) {
                setPreviewMonth(index);
            }
        }
    };

    const handleYearClick = (year) => {
        setPreviewYear(year);
        setView('months');
    };

    const handleMonthClick = (index) => {
        setPreviewMonth(index);
    };

    const handleDayClick = (day) => {
        const newDate = dayjs(new Date(previewYear, previewMonth, day)).format("YYYY-MM-DD");
        setTempSelectedDate(newDate);
    };

    const handleConfirm = () => {
        if (onChange && tempSelectedDate) {
            onChange({ target: { value: tempSelectedDate, name: 'birthday' } });
        }
        setShowModal(false);
        setTimeout(() => setView('years'), 300);
    };

    const daysInMonth = React.useMemo(() => dayjs(new Date(previewYear, previewMonth, 1)).daysInMonth(), [previewYear, previewMonth]);
    const firstDayOfWeek = React.useMemo(() => dayjs(new Date(previewYear, previewMonth, 1)).day(), [previewYear, previewMonth]); 
    const daysArray = React.useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);
    const blanksArray = React.useMemo(() => Array.from({ length: firstDayOfWeek }, (_, i) => i), [firstDayOfWeek]);

    const isSelected = (day) => {
        if (!tempSelectedDate) return false;
        const s = dayjs(tempSelectedDate, "YYYY-MM-DD");
        return s.year() === previewYear && s.month() === previewMonth && s.date() === day;
    };

    let displayTitle = `${fullMonths[previewMonth]} ${previewYear}`;
    let displaySubtitle = "Pick a day";
    
    if (tempSelectedDate) {
        const s = dayjs(tempSelectedDate, "YYYY-MM-DD");
        if (s.year() === previewYear && s.month() === previewMonth) {
            displayTitle = s.format("D MMMM YYYY");
            displaySubtitle = "Date selected";
        }
    }

    const halfPadding = Math.max(0, (listContainerHeight / 2) - (ITEM_HEIGHT / 2));

    if (!isMobile) {
        return (
            <Input 
                type="date"
                label={label}
                labelClassName={labelClassName}
                value={value}
                onChange={onChange}
                className={error ? "border-red-500" : ""}
                style={{ 
                    backgroundColor: mode === "dark" ? "rgba(30, 41, 59, 0.5)" : "#ffffff", 
                    borderColor: error ? "#ef4444" : (mode === "dark" ? "#334155" : "#e2e8f0"), 
                    color: mode === "dark" ? "#f1f5f9" : "#0f172a" 
                }}
                disabled={disabled}
                max={maxDate}
            />
        );
    }

    return (
        <>
            <div className="relative group cursor-pointer" onClick={(e) => { e.preventDefault(); if(!disabled) setShowModal(true); }}>
                {/* Invisible overlay ensures the virtual keyboard is never accidentally summoned! */}
                <div className="absolute inset-x-0 bottom-0 top-[26px] z-10 cursor-pointer"></div>
                <Input 
                    type="text"
                    readOnly
                    tabIndex="-1"
                    label={label}
                    labelClassName={labelClassName}
                    value={value ? dayjs(value).format('DD MMM, YYYY') : ''}
                    placeholder="Select Birthday"
                    className={`cursor-pointer ${error ? "border-red-500" : ""}`}
                    style={{ 
                        backgroundColor: mode === "dark" ? "rgba(30, 41, 59, 0.5)" : "#ffffff", 
                        borderColor: error ? "#ef4444" : (mode === "dark" ? "#334155" : "#e2e8f0"), 
                        color: mode === "dark" ? "#f1f5f9" : "#0f172a" 
                    }}
                    disabled={disabled}
                />
            </div>

            {showModal && (
                <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 ${mode === 'dark' ? 'dark' : ''} transition-all`}>
                    <div className="w-full max-w-[360px] h-[75vh] max-h-[550px] flex flex-col bg-white dark:bg-slate-900 rounded-3xl overflow-hidden shadow-2xl">

                        <header className="flex-none px-4 py-4 flex items-center justify-between border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 z-10">
                            <Heading variant="h4" className="text-xl font-bold text-slate-800 dark:text-slate-100">Select Birthday</Heading>
                            <button type="button" onClick={() => setShowModal(false)} className="p-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                <X size={24} />
                            </button>
                        </header>

                        <div className="flex-1 flex overflow-hidden relative">
                        {/* Left - Calendar Grid */}
                        <div className="flex-1 h-full flex flex-col pt-6 overflow-y-auto">
                            <div className="px-4 pb-6 text-center">
                                <Text className="text-2xl sm:text-3xl font-bold text-brand whitespace-nowrap tracking-tight">{displayTitle}</Text>
                                <Text className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-1">{displaySubtitle}</Text>
                            </div>

                            <div className="grid grid-cols-7 gap-1 px-4 mb-3">
                                {['S','M','T','W','T','F','S'].map((d, i) => (
                                    <div key={i} className="text-center text-xs font-bold text-slate-400">{d}</div>
                                ))}
                            </div>

                            <div className="grid grid-cols-7 gap-1 px-4 pb-8">
                                {blanksArray.map((_, i) => (
                                    <div key={`blank-${i}`} className="aspect-square"></div>
                                ))}
                                {daysArray.map((day) => {
                                    const selected = isSelected(day);
                                    return (
                                        <button
                                            key={day}
                                            type="button"
                                            onClick={() => handleDayClick(day)}
                                            className={`aspect-square flex items-center justify-center rounded-full text-[15px] transition-all duration-200 ${
                                                selected 
                                                ? 'bg-brand !text-white font-bold shadow-lg shadow-brand/40 scale-110' 
                                                : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-95 font-medium'
                                            }`}
                                        >
                                            {day}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 25% Right - Scroller */}
                        <div className="w-[30%] h-full border-l border-slate-200 dark:border-slate-800 flex flex-col bg-slate-50 dark:bg-slate-900/50 relative shadow-inner">
                            {view === 'months' ? (
                                <button 
                                    type="button"
                                    onClick={() => setView('years')}
                                    className="flex items-center justify-center p-3 border-b border-slate-200 dark:border-slate-800 text-slate-500 hover:text-brand bg-white dark:bg-slate-800 transition-colors z-20 shadow-sm"
                                >
                                    <ChevronLeft size={20} className="mr-1"/>
                                    <span className="text-[11px] font-bold uppercase tracking-wider">{previewYear}</span>
                                </button>
                            ) : (
                                <button 
                                    type="button"
                                    onClick={() => setView('months')}
                                    className="flex items-center justify-center p-3 border-b border-slate-200 dark:border-slate-800 text-slate-500 hover:text-brand bg-white dark:bg-slate-800 transition-colors z-20 shadow-sm"
                                >
                                    <span className="text-[11px] font-bold uppercase tracking-wider">{months[previewMonth]}</span>
                                    <ChevronLeft size={20} className="ml-1 rotate-180"/>
                                </button>
                            )}
                            
                            <div className="flex-1 relative overflow-hidden">
                                {/* Visual Center Highlight securely bound to the scrolling wrapper */}
                                <div className="absolute inset-x-0 top-1/2 -mt-[28px] pointer-events-none z-0">
                                    <div className="h-[56px] border-y-2 border-brand/40 bg-brand/[0.08]" />
                                </div>

                                <div 
                                    ref={rightScrollRef}
                                    className="h-full overflow-y-auto no-scrollbar relative z-10"
                                    onScroll={handleScroll}
                                    style={{ scrollSnapType: 'y mandatory' }}
                                >
                                    <div style={{ height: halfPadding }}></div>
                                
                                {view === 'years' ? (
                                    years.map((year) => {
                                        const isCentered = year === previewYear;
                                        return (
                                            <div 
                                                key={year}
                                                onClick={() => handleYearClick(year)}
                                                className="flex items-center justify-center cursor-pointer transition-all duration-300"
                                                style={{ height: ITEM_HEIGHT, scrollSnapAlign: 'center' }}
                                            >
                                                <span className={`text-[15px] transition-all duration-300 ${isCentered ? 'text-brand scale-125 font-bold' : 'text-slate-400 dark:text-slate-500 scale-100 font-medium'}`}>
                                                    {year}
                                                </span>
                                            </div>
                                        )
                                    })
                                ) : (
                                    months.map((month, index) => {
                                        const isCentered = index === previewMonth;
                                        return (
                                            <div 
                                                key={month}
                                                onClick={() => handleMonthClick(index)}
                                                className="flex items-center justify-center cursor-pointer transition-all duration-300"
                                                style={{ height: ITEM_HEIGHT, scrollSnapAlign: 'center' }}
                                            >
                                                <span className={`text-[15px] transition-all duration-300 ${isCentered ? 'text-brand scale-125 font-bold' : 'text-slate-400 dark:text-slate-500 scale-100 font-medium'}`}>
                                                    {month}
                                                </span>
                                            </div>
                                        )
                                    })
                                )}
                                
                                <div style={{ height: halfPadding }}></div>
                            </div>
                            </div>
                        </div>
                    </div>
                    {/* Footer - Confirm button */}
                    <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 border-none">
                        <Button 
                            type="button"
                            className="w-full"
                            onClick={handleConfirm}
                            disabled={!tempSelectedDate}
                        >
                            Confirm Date
                        </Button>
                    </div>
                    </div>
                </div>
            )}

            <style dangerouslySetInnerHTML={{__html: `
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}} />
        </>
    );
};
